<template>
  <!-- App -->
  <f7-app id="app" ref="app" :params="f7params">

    <!-- Statusbar -->
    <f7-statusbar></f7-statusbar>

    <!-- Left Panel -->
    <f7-panel left cover :color-theme="themeColor">
      <f7-view url="/panel-left"></f7-view>
    </f7-panel>

    <!-- Right Panel -->
    <f7-panel right cover :color-theme="themeColor">
      <f7-view url="/panel-right"></f7-view>
    </f7-panel>

    <!-- Main View -->
    <f7-view
      id="main-view"
      url="/"
      main
      :color-theme="themeColor"
      :pushState="true"
      :stackPages="true"
      :pushStateSeparator="pushStateSeparator"
      :pushStateRoot="pushStateRoot">
    </f7-view>

    <f7-routable-modals />
  </f7-app>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import routes from './routes'
import localforage from 'localforage'
import versionHelp from '@Util/uiapp/versionHelp'
import { playBumblebee, stopAudio, playNothing } from '@/service/audioPlayer'

export default {
  name: 'app',
  dependencies: [
    'baseUrl',
    'themeColor',
    'platformUtils',
    'userSignInStatusType'
  ],
  data () {
    return {
      pushStateSeparator: this.platformUtils.isWeb(process.env.VUE_APP_PLATFORM) ? '' : '#!',
      pushStateRoot: this.platformUtils.isWeb(process.env.VUE_APP_PLATFORM) ? this.baseUrl : document.location.pathname,
      f7params: {
        id: 'tw.ezmeals.store-management',
        name: 'EZMeals',
        theme: process.env.VUE_APP_THEME,
        routes,
        sortable: {
          moveElements: false
        },
        panel: {
          // leftBreakpoint: 768
        }
      }
    }
  },
  computed: {
    ...mapState(['socketId', 'fullscreen', 'notification']),
    ...mapState('orderModule', ['orderErrorMessage']),
    ...mapState('userModule', ['uid', 'signInStatus']),
    ...mapGetters(['storeId']),
    ...mapState(['showVersionDialog', 'showNewVersionDialog', 'showWelcomeDialog'])
  },
  watch: {
    fullscreen (fullscreen) {
      if (fullscreen) {
        this.$f7.dialog.confirm('是否進入全螢幕模式', '全螢幕', this.executeFullscreen, () => {
          this.setFullscreen(false)
        })
      }
    },
    showNewVersionDialog (version) {
      this.$f7.dialog.alert(`您的應用程式版本為 ${process.env.VUE_APP_VERSION} 請更新至最新版: ${version}`, '有新版本的應用程式', () => {
        // Framework7 3.x already disallowed router methods on the global app
        // router; must use the View's router. In dev (lockfile regenerated by
        // newer npm) the runtime check became fatal where it was previously
        // a warning. Switch to the main view's router.
        this.$f7.views.main.router.navigate('/', { reloadAll: true })
      })
    },
    showWelcomeDialog () {
      const path = window.location.pathname
      if (path.substring(path.length - 12) === 'work-station') {
        // 因為 work-station 頁面會自己關閉一個 dialog，所以要避免他關到這個而沒有關到他原本該關的 loading 頁面
        return
      }
      this.$f7.dialog.create({
        title: 'EZMeals',
        text: '歡迎使用 AI 服務生',
        buttons: [
          {
            text: '開始使用'
          }
        ],
        onClick () {
          playNothing()
        }
      }).open()
    },
    showVersionDialog () {
      var dialog = this.$f7.dialog.create({
        text: `新版本 ${process.env.VUE_APP_VERSION} 功能如下:`,
        content: versionHelp.getVersionDisplayContent(),
        buttons: [
          {
            text: '確定'
          }
        ],
        onClick () {
          playNothing()
        }
      })
      dialog.open()
    },
    orderErrorMessage (message) {
      if (message) {
        this.$f7.notification.create({
          title: '緊急呼叫',
          subtitle: '系統錯誤訊息',
          text: message,
          closeOnClick: true,
          closeButton: true
        }).open()
        this.setOrderErrorMessage(null)
      }
    },
    notification ({ type, status, msg }) {
      playBumblebee(true)
      this.$f7.notification.create({
        title: '收銀機呼叫',
        subtitle: '需要服務人員協助',
        text: msg,
        closeOnClick: true,
        closeButton: true,
        on: {
          close () {
            stopAudio()
          }
        }
      }).open()
    }
  },
  async created () {
    const settings = JSON.parse(await localforage.getItem('settings'))
    if (settings) {
      this.setSettings(settings)
    }
  },
  mounted () {
    ['', 'webkit', 'moz', 'ms'].forEach(prefix => {
      document.addEventListener(prefix + 'fullscreenchange', () => { this.setFullscreen(false) })
    })
  },
  methods: {
    ...mapActions(['setNotification']),
    ...mapActions('orderModule', ['setOrderErrorMessage']),
    ...mapActions(['setSettings', 'setFullscreen']),
    executeFullscreen () {
      const app = this.$refs.app.$el
      if (app.requestFullscreen) {
        app.requestFullscreen()
      } else if (app.mozRequestFullScreen) { /* Firefox */
        app.mozRequestFullScreen()
      } else if (app.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
        app.webkitRequestFullscreen()
      } else if (app.msRequestFullscreen) { /* IE/Edge */
        app.msRequestFullscreen()
      }
    }
  }
}
</script>

<style lang="scss">
@import url(//fonts.googleapis.com/earlyaccess/notosanstc.css);

#app {
  font-family: 'Noto Sans TC', Roboto, Noto, Helvetica, Arial, sans-serif;
}

/* Left Panel right border when it is visible by breakpoint */
.panel-left.panel-visible-by-breakpoint:before {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 1px;
  background: rgba(0,0,0,0.1);
  content: '';
  z-index: 6000;
}

/* Hide navbar link which opens left panel when it is visible by breakpoint */
.panel-left.panel-visible-by-breakpoint ~ .view .navbar .panel-open[data-panel="left"] {
  display: none;
}

/*
  Extra borders for main view and left panel for iOS theme when it behaves as panel (before breakpoint size)
*/
.ios .panel-left:not(.panel-visible-by-breakpoint).panel-active ~ .view-main:before,
.ios .panel-left:not(.panel-visible-by-breakpoint).panel-closing ~ .view-main:before {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 1px;
  background: rgba(0,0,0,0.1);
  content: '';
  z-index: 6000;
}
</style>
